import { Form, Input, Modal, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { putRiskIndicatorThresholdData } from '../../../redux/actions/crs/crsRiskIndicator';
import { CRS_CONSTANTS } from '../../constants/crsContants';
import { handleAddRuleNotification, usePrevious } from './util';

import { isEmpty } from '../../../util/lodashlike';
const { GET_DATA } = CRS_CONSTANTS;

const getRiskIndicatorOptions = (riskIndicators) => {
  if (Array.isArray(riskIndicators)) {
    const riskIndicatorOptions = riskIndicators.map((indicator) => (
      <Select.Option
        key={indicator.riskIndicator}
        value={indicator.riskIndicator}>
        {indicator.riskIndicator}
      </Select.Option>
    ));
    return riskIndicatorOptions;
  } else return [];
};

const getConditionOptions = (riskIndicators, selected) => {
  if (riskIndicators && selected) {
    const selectedIndicator = riskIndicators.find(
      (indicator) => indicator.riskIndicator == selected
    );
    return selectedIndicator.condition.map((c) => (
      <Select.Option key={c} value={c}>
        {c}
      </Select.Option>
    ));
  } else return [];
};

const FORM_INITIAL_VALUES = {
  riskIndicator: '',
  condition: '',
  value: ''
};

export const AddRuleModal = ({
  category,
  addRuleVisible,
  setAddRuleVisible,
  refreshRiskIndicatorThresholds,
  dispatch,
  riskIndicators,
  putRiskIndicatorThreshold
}) => {
  const [form] = Form.useForm();
  const [riskIndicator, setRiskIndicator] = useState('');
  const [formValid, setFormValid] = useState(true);
  const [inputPattern, setInputPattern] = useState(/^[\p{L}\p{N}\p{M}., ]+$/u);
  const [patternLabel, setPatternLabel] = useState('alphanumeric');

  const prevPutRiskIndicatorThreshold = usePrevious(putRiskIndicatorThreshold);

  const handleRiskIndicatorChange = (value) => {
    setRiskIndicator(value);
    form.setFieldsValue({
      condition: '',
      value: ''
    });
  };

  const handleRiskConditionChange = (value) => {
    form.setFieldsValue({
      value: ''
    });
    if (value === 'IN BETWEEN') {
      setInputPattern(/^\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*$/);
      setPatternLabel('comma separated numeric');
    } else {
      getInputPattern(riskIndicator);
    }
  };

  const getInputPattern = (value) => {
    const selectedRiskIndicator = (riskIndicators?.data || []).find(
      (indicator) => indicator.riskIndicator == value
    );

    switch (selectedRiskIndicator?.riskIndicatorType) {
      case 'Boolean':
        setInputPattern(/^([Tt][Rr][Uu][Ee]|[Ff][Aa][Ll][Ss][Ee])$/);
        setPatternLabel('boolean');
        break;
      case 'Numeric':
        setInputPattern(/^\d{0,9}(\.\d{0,6})?$/);
        setPatternLabel('numeric');
        break;
      case 'String':
        setInputPattern(/^[\p{L}\p{N}\p{M}, ]+$/u);
        setPatternLabel('alphanumeric');
        break;
      default:
        setInputPattern(/^[\p{L}\p{N}\p{M}., ]+$/u);
        setPatternLabel('alphanumeric');
    }
  };

  const handleAddRuleConfirm = (formValues) => {
    dispatch(
      putRiskIndicatorThresholdData(
        {
          ruleCategory: category,
          riskIndicator: formValues.riskIndicator,
          riskCondition: formValues.condition,
          riskValue: formValues.value
        },
        GET_DATA
      )
    );
  };

  const handleAddRuleCancel = () => {
    form.resetFields();
    setRiskIndicator('');
    setAddRuleVisible(false);
    setFormValid(true);
  };

  useEffect(() => {
    if (prevPutRiskIndicatorThreshold?.isLoading) {
      const { data, isLoading } = putRiskIndicatorThreshold;
      if (!isEmpty(data) && !isLoading) {
        refreshRiskIndicatorThresholds();
        handleAddRuleNotification(data);
        handleAddRuleCancel();
      }
    }
  }, [putRiskIndicatorThreshold]);

  const updateButtonStatus = () => {
    setFormValid(
      form.getFieldValue('riskIndicator') === '' ||
        form.getFieldValue('condition') === '' ||
        form.getFieldValue('value') === '' ||
        form.getFieldError('value').length > 0
    );
  };

  return (
    <Modal
      title="Add Rule"
      visible={addRuleVisible}
      okButtonProps={{
        loading: putRiskIndicatorThreshold.isLoading,
        disabled: putRiskIndicatorThreshold.isLoading || formValid
      }}
      onCancel={handleAddRuleCancel}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            form.resetFields();
            handleAddRuleConfirm(values);
          })
          .catch((info) => {});
      }}>
      <Form
        size="small"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 17 }}
        form={form}
        onFieldsChange={updateButtonStatus}
        initialValues={FORM_INITIAL_VALUES}>
        <Form.Item
          label="Risk Indicator"
          name="riskIndicator"
          rules={[{ required: true, message: 'Risk indicator is required' }]}>
          <Select
            onChange={handleRiskIndicatorChange}
            loading={riskIndicators.isLoading}>
            {getRiskIndicatorOptions(riskIndicators.data)}
          </Select>
        </Form.Item>
        <Form.Item
          label="Condition"
          name="condition"
          rules={[{ required: true, message: 'Condition is required' }]}>
          <Select onChange={handleRiskConditionChange}>
            {getConditionOptions(riskIndicators.data, riskIndicator)}
          </Select>
        </Form.Item>
        <Form.Item
          label="Value"
          name="value"
          rules={[
            { required: true, message: 'Value is required' },
            {
              pattern: inputPattern,
              message: `This field ${
                patternLabel === 'alphanumeric'
                  ? 'cannot start with special character'
                  : `can only contain ${patternLabel} values`
              }. Please enter valid information.`
            }
          ]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

AddRuleModal.propTypes = {
  category: PropTypes.string,
  addRuleVisible: PropTypes.bool,
  setAddRuleVisible: PropTypes.func,
  refreshRiskIndicatorThresholds: PropTypes.func,
  dispatch: PropTypes.func,
  riskIndicators: PropTypes.object,
  putRiskIndicatorThreshold: PropTypes.object
};

function mapStateToProps(state, ownProps = {}) {
  return {
    putRiskIndicatorThreshold: state.crsRiskIndicator.putRiskIndicatorThreshold
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddRuleModal);
